import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type Player = 'X' | 'O' | null;
type Board = Player[];

export function TicTacToe() {
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [winner, setWinner] = useState<Player | 'draw' | null>(null);
  const [winningLine, setWinningLine] = useState<number[]>([]);
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 });
  const [gameMode, setGameMode] = useState<'pvp' | 'ai' | null>(null);
  const [difficulty, setDifficulty] = useState<'easy' | 'hard'>('easy');

  const winningCombinations = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
    [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
    [0, 4, 8], [2, 4, 6] // Diagonals
  ];

  useEffect(() => {
    const result = calculateWinner(board);
    if (result) {
      setWinner(result.winner);
      setWinningLine(result.line || []);
      
      // Update scores
      if (result.winner === 'X') {
        setScores(prev => ({ ...prev, X: prev.X + 1 }));
      } else if (result.winner === 'O') {
        setScores(prev => ({ ...prev, O: prev.O + 1 }));
      } else if (result.winner === 'draw') {
        setScores(prev => ({ ...prev, draws: prev.draws + 1 }));
      }
    }
  }, [board]);

  // AI move
  useEffect(() => {
    if (gameMode === 'ai' && !isXNext && !winner && board.some(cell => cell !== null)) {
      const timer = setTimeout(() => {
        makeAIMove();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isXNext, winner, gameMode, board]);

  const calculateWinner = (squares: Board): { winner: Player | 'draw'; line?: number[] } | null => {
    // Check for winner
    for (let combo of winningCombinations) {
      const [a, b, c] = combo;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line: combo };
      }
    }
    
    // Check for draw
    if (squares.every(cell => cell !== null)) {
      return { winner: 'draw' };
    }
    
    return null;
  };

  const makeAIMove = () => {
    if (difficulty === 'hard') {
      makeSmartAIMove();
    } else {
      makeRandomAIMove();
    }
  };

  const makeRandomAIMove = () => {
    const availableSpots = board.map((cell, index) => cell === null ? index : null).filter(val => val !== null) as number[];
    if (availableSpots.length > 0) {
      const randomIndex = availableSpots[Math.floor(Math.random() * availableSpots.length)];
      handleClick(randomIndex);
    }
  };

  const makeSmartAIMove = () => {
    // Minimax algorithm for unbeatable AI
    const bestMove = minimax(board, 'O');
    if (bestMove.index !== undefined) {
      handleClick(bestMove.index);
    }
  };

  const minimax = (newBoard: Board, player: 'X' | 'O'): { score: number; index?: number } => {
    const availSpots = newBoard.map((cell, index) => cell === null ? index : null).filter(val => val !== null) as number[];
    
    const result = calculateWinner(newBoard);
    if (result?.winner === 'X') return { score: -10 };
    if (result?.winner === 'O') return { score: 10 };
    if (result?.winner === 'draw') return { score: 0 };

    const moves: { index: number; score: number }[] = [];

    for (let i = 0; i < availSpots.length; i++) {
      const move: { index: number; score: number } = { index: availSpots[i], score: 0 };
      const newBoardCopy = [...newBoard];
      newBoardCopy[availSpots[i]] = player;

      if (player === 'O') {
        const result = minimax(newBoardCopy, 'X');
        move.score = result.score;
      } else {
        const result = minimax(newBoardCopy, 'O');
        move.score = result.score;
      }

      moves.push(move);
    }

    let bestMove: number = 0;
    if (player === 'O') {
      let bestScore = -10000;
      for (let i = 0; i < moves.length; i++) {
        if (moves[i].score > bestScore) {
          bestScore = moves[i].score;
          bestMove = i;
        }
      }
    } else {
      let bestScore = 10000;
      for (let i = 0; i < moves.length; i++) {
        if (moves[i].score < bestScore) {
          bestScore = moves[i].score;
          bestMove = i;
        }
      }
    }

    return moves[bestMove];
  };

  const handleClick = (index: number) => {
    if (board[index] || winner) return;
    
    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);
    setWinningLine([]);
  };

  const resetAll = () => {
    resetGame();
    setScores({ X: 0, O: 0, draws: 0 });
    setGameMode(null);
  };

  const renderSquare = (index: number) => {
    const isWinning = winningLine.includes(index);
    const value = board[index];
    
    return (
      <button
        key={index}
        onClick={() => handleClick(index)}
        disabled={gameMode === 'ai' && !isXNext}
        className={`
          aspect-square w-full text-6xl font-bold rounded-lg
          transition-all duration-200 transform hover:scale-105
          ${!value && !winner ? 'bg-white hover:bg-gray-100 cursor-pointer' : 'bg-white'}
          ${value === 'X' ? 'text-blue-600' : 'text-red-600'}
          ${isWinning ? 'bg-green-200 ring-4 ring-green-500 animate-pulse' : ''}
          ${winner && !value ? 'opacity-50' : ''}
          shadow-lg
        `}
      >
        {value}
      </button>
    );
  };

  // Mode selection screen
  if (!gameMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-5xl font-bold text-center mb-8 text-gray-800">
            ⭕ Tic-Tac-Toe ❌
          </h1>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-gray-700">Select Game Mode</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setGameMode('pvp')}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-8 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  <div className="text-4xl mb-2">👥</div>
                  <div className="text-2xl font-bold">Player vs Player</div>
                  <div className="text-sm mt-2 opacity-90">Play with a friend</div>
                </button>
                
                <button
                  onClick={() => setGameMode('ai')}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white p-8 rounded-xl hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  <div className="text-4xl mb-2">🤖</div>
                  <div className="text-2xl font-bold">Player vs AI</div>
                  <div className="text-sm mt-2 opacity-90">Challenge the computer</div>
                </button>
              </div>
            </div>

            {gameMode === 'ai' && (
              <div>
                <h2 className="text-2xl font-semibold mb-4 text-gray-700">AI Difficulty</h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setDifficulty('easy')}
                    className={`p-4 rounded-lg font-semibold transition-all ${
                      difficulty === 'easy'
                        ? 'bg-green-500 text-white ring-4 ring-green-300'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    😊 Easy
                  </button>
                  <button
                    onClick={() => setDifficulty('hard')}
                    className={`p-4 rounded-lg font-semibold transition-all ${
                      difficulty === 'hard'
                        ? 'bg-red-500 text-white ring-4 ring-red-300'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    😈 Hard (Unbeatable)
                  </button>
                </div>
              </div>
            )}

            {/* <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors"
            >
              ← Back to Home
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-5xl font-bold text-white mb-2">Tic-Tac-Toe</h1>
          <p className="text-white text-lg">
            {gameMode === 'ai' ? `Playing against AI (${difficulty})` : 'Player vs Player'}
          </p>
        </div>

        {/* Score Board */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600">{scores.X}</div>
              <div className="text-sm text-gray-600">Player X</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-600">{scores.draws}</div>
              <div className="text-sm text-gray-600">Draws</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-red-600">{scores.O}</div>
              <div className="text-sm text-gray-600">{gameMode === 'ai' ? 'AI' : 'Player O'}</div>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 text-center">
          {winner ? (
            <div className="text-2xl font-bold">
              {winner === 'draw' ? (
                <span className="text-gray-600">🤝 It's a Draw!</span>
              ) : (
                <span className={winner === 'X' ? 'text-blue-600' : 'text-red-600'}>
                  🎉 {winner} Wins!
                </span>
              )}
            </div>
          ) : (
            <div className="text-2xl font-bold">
              <span className={isXNext ? 'text-blue-600' : 'text-red-600'}>
                {isXNext ? '❌ X' : '⭕ O'}'s Turn
                {gameMode === 'ai' && !isXNext && ' (AI thinking...)'}
              </span>
            </div>
          )}
        </div>

        {/* Game Board */}
        <div className="bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl shadow-2xl p-6 mb-6">
          <div className="grid grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(index => renderSquare(index))}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <button
            onClick={resetGame}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-lg text-xl font-semibold transition-colors shadow-lg"
          >
            🔄 New Game
          </button>
          
          <button
            onClick={resetAll}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
          >
            Change Mode
          </button>
          
          {/* <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-700 hover:bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
          >
            ← Back to Home
          </button> */}
        </div>
      </div>
    </div>
  );
}

